/**
 * JobExecutionHistory Component
 *
 * Display execution history and logs for a scheduled job.
 * Uses Standard Component Library throughout.
 */

import React, { useState, useMemo } from 'react';
import {
  StandardDataTable,
  StandardCard,
  StandardEmptyState,
  StandardSearchBar,
  StandardButton,
  StandardModal,
  type TableColumn
} from '../../standard';
import {
  CheckCircle,
  XCircle,
  Clock,
  PlayCircle,
  AlertCircle,
  Eye
} from 'lucide-react';
import { scheduledJobsStorage } from '../../../services/scheduledJobsStorage';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { JobExecution } from '../../../types/scheduledJobs';

interface JobExecutionHistoryProps {
  jobId: string;
  jobName?: string;
  showSearch?: boolean;
  maxHeight?: string;
}

export const JobExecutionHistory: React.FC<JobExecutionHistoryProps> = ({
  jobId,
  jobName,
  showSearch = true,
  maxHeight = '600px'
}) => {
  const [executions] = useState<JobExecution[]>(() =>
    USE_GOBODO_API ? [] : scheduledJobsStorage.getJobExecutions(jobId)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExecution, setSelectedExecution] = useState<JobExecution | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Filter executions
  const filteredExecutions = useMemo(() => {
    if (!searchQuery) return executions;

    const query = searchQuery.toLowerCase();
    return executions.filter(exec =>
      exec.status.toLowerCase().includes(query) ||
      exec.errorMessage?.toLowerCase().includes(query)
    );
  }, [executions, searchQuery]);

  // Format duration
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  };

  // View execution details
  const handleViewDetails = (execution: JobExecution) => {
    setSelectedExecution(execution);
    setIsDetailModalOpen(true);
  };

  // Table columns
  const columns: TableColumn<JobExecution>[] = [
    {
      key: 'startTime',
      label: 'Start Time',
      sortable: true,
      render: (value) => (
        <div className="text-sm">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {new Date(value).toLocaleDateString()}
          </div>
          <div className="text-gray-500 dark:text-gray-400">
            {new Date(value).toLocaleTimeString()}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, exec) => {
        const statusConfig = {
          running: {
            bg: 'bg-yellow-100 dark:bg-yellow-900/30',
            text: 'text-yellow-800 dark:text-yellow-200',
            icon: Clock
          },
          completed: {
            bg: 'bg-green-100 dark:bg-green-900/30',
            text: 'text-green-800 dark:text-green-200',
            icon: CheckCircle
          },
          failed: {
            bg: 'bg-red-100 dark:bg-red-900/30',
            text: 'text-red-800 dark:text-red-200',
            icon: XCircle
          }
        };

        const config = statusConfig[exec.status];
        const StatusIcon = config.icon;

        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            <StatusIcon size={12} />
            {exec.status}
          </span>
        );
      }
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (_, exec) => {
        if (!exec.duration) return <span className="text-gray-400">-</span>;
        return (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {formatDuration(exec.duration)}
          </span>
        );
      }
    },
    {
      key: 'recordsProcessed',
      label: 'Records',
      render: (value) => {
        if (value === undefined || value === null) return <span className="text-gray-400">-</span>;
        return (
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {value.toLocaleString()}
          </span>
        );
      }
    },
    {
      key: 'executedBy',
      label: 'Executed By',
      render: (value, exec) => (
        <div className="flex items-center gap-1.5">
          <PlayCircle size={14} className="text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
            {value}
            {exec.triggeredBy && ` (${exec.triggeredBy})`}
          </span>
        </div>
      )
    },
    {
      key: 'id',
      label: '',
      render: (_, exec) => (
        <StandardButton
          size="xs"
          variant="ghost"
          leftIcon={<Eye size={14} />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewDetails(exec);
          }}
        >
          Details
        </StandardButton>
      )
    }
  ];

  return (
    <>
      <StandardCard>
        {jobName && (
          <StandardCard.Header
            title="Execution History"
            subtitle={`For job: ${jobName}`}
          />
        )}
        <StandardCard.Body className="space-y-4">
          {showSearch && (
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search executions by status or error..."
            />
          )}

          <div style={{ maxHeight, overflowY: 'auto' }}>
            {filteredExecutions.length === 0 && !searchQuery ? (
              <StandardEmptyState
                icon={<Clock size={48} />}
                title="No execution history"
                message="This job hasn't been executed yet"
              />
            ) : filteredExecutions.length === 0 ? (
              <StandardEmptyState
                icon={<AlertCircle size={48} />}
                title="No executions found"
                message={`No executions match "${searchQuery}"`}
              />
            ) : (
              <StandardDataTable
                columns={columns}
                data={filteredExecutions}
                onRowClick={handleViewDetails}
              />
            )}
          </div>
        </StandardCard.Body>
      </StandardCard>

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <StandardModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title="Execution Details"
          size="lg"
        >
          <StandardModal.Body>
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </label>
                <div className="mt-1">
                  {selectedExecution.status === 'completed' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                      <CheckCircle size={14} />
                      Completed
                    </span>
                  )}
                  {selectedExecution.status === 'failed' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                      <XCircle size={14} />
                      Failed
                    </span>
                  )}
                  {selectedExecution.status === 'running' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200">
                      <Clock size={14} />
                      Running
                    </span>
                  )}
                </div>
              </div>

              {/* Timing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Time
                  </label>
                  <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {new Date(selectedExecution.startTime).toLocaleString()}
                  </div>
                </div>

                {selectedExecution.endTime && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      End Time
                    </label>
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {new Date(selectedExecution.endTime).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {/* Duration & Records */}
              <div className="grid grid-cols-2 gap-4">
                {selectedExecution.duration && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Duration
                    </label>
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {formatDuration(selectedExecution.duration)}
                    </div>
                  </div>
                )}

                {selectedExecution.recordsProcessed !== undefined && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Records Processed
                    </label>
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {selectedExecution.recordsProcessed.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>

              {/* Execution Info */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Executed By
                </label>
                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                  {selectedExecution.executedBy}
                  {selectedExecution.triggeredBy && ` (User: ${selectedExecution.triggeredBy})`}
                </div>
              </div>

              {/* Error Details */}
              {selectedExecution.status === 'failed' && (
                <>
                  {selectedExecution.errorMessage && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Error Message
                      </label>
                      <div className="mt-1 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <p className="text-sm text-red-800 dark:text-red-200">
                          {selectedExecution.errorMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedExecution.errorDetails && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Error Details
                      </label>
                      <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md">
                        <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                          {selectedExecution.errorDetails}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </StandardModal.Body>
          <StandardModal.Footer>
            <StandardButton
              variant="secondary"
              onClick={() => setIsDetailModalOpen(false)}
            >
              Close
            </StandardButton>
          </StandardModal.Footer>
        </StandardModal>
      )}
    </>
  );
};

export default JobExecutionHistory;
